/**
 * DashboardSection — Collapsible section wrapper for Clinical Day View.
 *
 * Shows heading with count badge and optional actions slot.
 * Click header to toggle collapsed state.
 * Sortable column headers for Name / Last Contact.
 * Scrolls when content exceeds the fixed card height.
 */
import React from "react";
import { ChevronDown, ArrowUpAZ, ArrowDownZA, CalendarArrowUp, CalendarArrowDown } from "lucide-react";

const ICON_SIZE = 10;

const sortIcon = (field, sortField, sortDirection) => {
    if (sortField !== field) return null;
    if (field === "name") {
        return sortDirection === "asc"
            ? <ArrowUpAZ size={ICON_SIZE} className="cdv-sort-icon" />
            : <ArrowDownZA size={ICON_SIZE} className="cdv-sort-icon" />;
    }
    if (field === "date") {
        return sortDirection === "asc"
            ? <CalendarArrowUp size={ICON_SIZE} className="cdv-sort-icon" />
            : <CalendarArrowDown size={ICON_SIZE} className="cdv-sort-icon" />;
    }
    return null;
};

const DashboardSection = ({
    title,
    count = 0,
    collapsed = false,
    onToggleCollapse,
    actions = null,
    columnHeader = null,
    sortField = null,
    sortDirection = "asc",
    onSort,
    children,
}) => {
    const handleHeaderClick = () => onToggleCollapse?.(!collapsed);

    const handleSortClick = (field) => (e) => {
        e.stopPropagation();
        if (!onSort) return;
        if (sortField === field) {
            onSort(field, sortDirection === "asc" ? "desc" : "asc");
        } else {
            onSort(field, field === "date" ? "desc" : "asc");
        }
    };

    return (
        <div className="cdv-dashboard-section">
            <div
                className="cdv-dashboard-section-header"
                onClick={handleHeaderClick}
            >
                <h2 className="cdv-section-heading">
                    {title}
                    {count > 0 && (
                        <span className="cdv-section-count">{count}</span>
                    )}
                </h2>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {actions}
                    <ChevronDown
                        size={16}
                        className={`cdv-dashboard-section-chevron${collapsed ? " collapsed" : ""}`}
                    />
                </div>
            </div>
            <div className={`cdv-dashboard-section-body${collapsed ? " collapsed" : ""}`}>
                {columnHeader && (
                    <div className="cdv-column-header">
                        <span
                            className={`cdv-column-header-sortable${sortField === "name" ? " active" : ""}`}
                            onClick={handleSortClick("name")}
                        >
                            Name {sortIcon("name", sortField, sortDirection)}
                        </span>
                        <span
                            className={`cdv-column-header-sortable${sortField === "date" ? " active" : ""}`}
                            onClick={handleSortClick("date")}
                        >
                            Last Contact {sortIcon("date", sortField, sortDirection)}
                        </span>
                    </div>
                )}
                <div className="cdv-section-scroll-container">
                    {children}
                </div>
            </div>
        </div>
    );
};

export default DashboardSection;
